/**
 * Shared API error message extraction utilities.
 * Converts raw Frappe error responses into clean, user-facing strings.
 * Never exposes Python tracebacks, exc_type, or internal exception details.
 */

const STATUS_MESSAGES = {
  400: 'Bad request. Please check your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'Conflict detected. Please refresh and try again.',
  417: 'Validation error. Please check your input and try again.',
  500: 'Server error. Please try again in a moment.',
  503: 'Service unavailable. Please try again later.',
};

const FALLBACK = 'Something went wrong. Please try again.';

/**
 * Extract a user-friendly message from a Frappe _server_messages string.
 * Returns null if parsing fails or yields nothing useful.
 */
export function fromServerMessages(raw) {
  if (!raw) return null;
  try {
    const outer = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const first = Array.isArray(outer) ? outer[0] : outer;
    if (!first) return null;
    const inner = typeof first === 'string' ? JSON.parse(first) : first;
    const msg = inner?.message;
    if (typeof msg === 'string' && msg.trim()) return msg.trim().replace(/<[^>]*>/g, '');
  } catch {
    // ignore parse failures
  }
  return null;
}

/**
 * Return true if `str` looks like a Python traceback (not user-facing).
 */
function looksLikeTraceback(str) {
  if (typeof str !== 'string') return false;
  return (
    str.includes('Traceback (most recent call last)') ||
    str.includes('frappe.exceptions.') ||
    str.startsWith('["Traceback')
  );
}

/**
 * Extract a clean, user-facing error message from an axios error object.
 *
 * Priority:
 *  1. _server_messages (Frappe's structured user message)
 *  2. message.message (nested Frappe message objects)
 *  3. message (top-level string, if not a traceback)
 *  4. HTTP status code fallback
 *  5. Generic fallback
 */
export function extractErrorMessage(error) {
  if (!error) return FALLBACK;

  // Network/timeout — no response received
  if (error.request && !error.response) {
    if (error.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
    return 'Network error. Please check your connection and try again.';
  }

  const data = error.response?.data;
  const status = error.response?.status;

  if (!data) return STATUS_MESSAGES[status] || FALLBACK;

  // 1. _server_messages — most reliable user-facing message from Frappe
  const fromServer = fromServerMessages(data._server_messages);
  if (fromServer) return fromServer;

  // 2. Nested message.message object
  const nestedMsg =
    data?.message?.message ||
    data?.message?.error;
  if (typeof nestedMsg === 'string' && nestedMsg.trim() && !looksLikeTraceback(nestedMsg)) {
    return nestedMsg.trim();
  }

  // 3. Top-level message string (skip tracebacks and exception class strings)
  const topMsg = data?.message;
  if (
    typeof topMsg === 'string' &&
    topMsg.trim() &&
    !looksLikeTraceback(topMsg) &&
    !topMsg.startsWith('frappe.exceptions.')
  ) {
    return topMsg.trim();
  }

  // 4. Message is an object with an error key (e.g. { error: "...", error_type: "..." })
  if (data?.message && typeof data.message === 'object' && !Array.isArray(data.message)) {
    const objError =
      data.message.error ||
      data.message.message ||
      data.message.exception ||
      Object.values(data.message).find(v => typeof v === 'string' && !looksLikeTraceback(v));
    if (typeof objError === 'string' && objError.trim() && !looksLikeTraceback(objError)) {
      return objError.trim();
    }
  }

  // 5. Top-level error/exception field (e.g. { error: "...", error_type: "..." })
  const topError = data?.error || data?.exception;
  if (typeof topError === 'string' && topError.trim() && !looksLikeTraceback(topError)) {
    return topError.trim();
  }

  // 6. Top-level message string as last resort before HTTP fallback
  const dataMsg = data?.message;
  if (typeof dataMsg === 'string' && dataMsg.trim() && !looksLikeTraceback(dataMsg)) {
    return dataMsg.trim();
  }

  // 7. HTTP status
  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];

  return FALLBACK;
}

export default extractErrorMessage;
